import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import './StationActivityDashboard.css';

/**
 * Station Activity -- manager view.
 *
 * Shows productivity DURING active minutes, not raw throughput and not claimed cart
 * times. Claimed times rank scan habits: one operator can look 3x faster on claimed
 * seconds-per-unit and be slower once you divide by the minutes they were actually
 * active, because a quick scan-in/scan-out followed by idle time is invisible to
 * claimed time and expensive in active time.
 *
 * Deliberately shows starvation and scan-integrity side by side. Low active minutes
 * is ambiguous -- either the scans do not reflect the work, or the station had
 * nothing staged -- and showing one reading without the other invites blaming a
 * person for a scheduling problem.
 */

const TABS = [
  { key: 'stations',  label: 'Stations' },
  { key: 'operators', label: 'Operators' },
  { key: 'flags',     label: 'Scan integrity' },
  { key: 'pours',     label: 'Pours' }
];

const RANGES = [
  { days: 1,  label: 'Today' },
  { days: 7,  label: '7 days' },
  { days: 30, label: '30 days' },
  { days: 90, label: '90 days' }
];

const num = (v, dp = 0) =>
  v === null || v === undefined || v === '' ? '--' : Number(v).toFixed(dp);
const int = (v) =>
  v === null || v === undefined ? '--' : Number(v).toLocaleString();
const pct = (v) =>
  v === null || v === undefined ? '--' : `${Math.round(Number(v) * 100)}%`;
const time = (ts) =>
  ts ? new Date(ts).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : '--';

function StationActivityDashboard() {
  const [tab, setTab] = useState('stations');
  const [days, setDays] = useState(7);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [summary, setSummary] = useState(null);
  const [floor, setFloor] = useState([]);
  const [stations, setStations] = useState([]);
  const [operators, setOperators] = useState([]);
  const [flags, setFlags] = useState([]);
  const [pours, setPours] = useState([]);
  const [pauses, setPauses] = useState([]);
  const [rates, setRates] = useState([]);
  const [norms, setNorms] = useState([]);

  const load = useCallback(async () => {
    setError(null);
    try {
      // axios, not fetch: App.js installs a request interceptor that attaches the
      // Supabase Bearer token, and every endpoint below sits behind verifyAuth
      // because it reports on people. Bare fetch() would 401.
      const get = async (path) => (await axios.get(path)).data;
      const [s, f, st, op, fl, po, pa, ra, no] = await Promise.all([
        get(`/api/station-activity/summary?days=${days}`),
        get('/api/station-activity/floor'),
        get(`/api/station-activity/daily?days=${days}`),
        get(`/api/station-activity/operators?days=${days}`),
        get(`/api/station-activity/flags?days=${days}`),
        get(`/api/station-activity/pour/sessions?days=${days}`),
        get(`/api/station-activity/pour/pauses?days=${days}`),
        get('/api/station-activity/rates?all=true'),
        get('/api/station-activity/norms')
      ]);
      setSummary(s); setFloor(f); setStations(st);
      setOperators(op); setFlags(fl); setPours(po); setPauses(pa);
      setRates(ra); setNorms(no);
    } catch (err) {
      console.error('Error loading station activity:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, [load]);

  // Roll the per-day operator rows up to one row per person for the range.
  const operatorTotals = React.useMemo(() => {
    const byName = new Map();
    for (const r of operators) {
      const cur = byName.get(r.operator_name) || {
        operator_name: r.operator_name, days: 0, active_minutes: 0, span_minutes: 0,
        units: 0, carts: 0, measurable_carts: 0, claimed_minutes: 0,
        instant_closes: 0, impossible_rates: 0
      };
      cur.days += 1;
      cur.measurable_carts += Number(r.measurable_carts || 0);
      cur.active_minutes += Number(r.active_minutes || 0);
      cur.span_minutes += Number(r.span_minutes || 0);
      cur.units += Number(r.units || 0);
      cur.carts += Number(r.carts || 0);
      cur.claimed_minutes += Number(r.claimed_minutes || 0);
      cur.instant_closes += Number(r.instant_closes || 0);
      cur.impossible_rates += Number(r.impossible_rates || 0);
      byName.set(r.operator_name, cur);
    }
    return [...byName.values()]
      .map((r) => ({
        ...r,
        active_seconds_per_unit: r.units > 0 ? (r.active_minutes * 60) / r.units : null,
        units_per_active_minute: r.active_minutes > 0 ? r.units / r.active_minutes : null,
        claimed_seconds_per_unit: r.units > 0 ? (r.claimed_minutes * 60) / r.units : null,
        utilization: r.span_minutes > 0 ? r.active_minutes / r.span_minutes : null
      }))
      .sort((a, b) => b.units - a.units);
  }, [operators]);

  if (loading) {
    return (
      <div className="sa-page">
        <div className="sa-empty">Loading station activity...</div>
      </div>
    );
  }

  return (
    <div className="sa-page">
      <div className="sa-header">
        <div>
          <h1>Station Activity</h1>
          <p className="sa-sub">
            Productivity during active minutes. Active minutes come from the gaps
            between scans, capped at {summary?.grace_minutes ?? 10} minutes, so idle
            time costs what it actually costs.
          </p>
        </div>
        <div className="sa-ranges">
          {RANGES.map((r) => (
            <button
              key={r.days}
              className={`sa-range ${days === r.days ? 'active' : ''}`}
              onClick={() => setDays(r.days)}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {error && <div className="sa-error">Could not load: {error}</div>}

      <div className="sa-tiles">
        <Tile label="Units" value={int(summary?.total_units)} />
        <Tile label="Active minutes" value={int(Math.round(summary?.total_active_minutes || 0))} />
        <Tile
          label="Units / active minute"
          value={num(summary?.units_per_active_minute, 2)}
          note="the accountability metric"
        />
        <Tile
          label="Median utilization"
          value={pct(summary?.median_utilization)}
          note="active vs. first-to-last scan"
        />
        <Tile
          label="Flagged carts"
          value={int(summary?.flagged_carts)}
          note={`${int(summary?.instant_closes)} instant closes`}
          warn={Number(summary?.flagged_carts) > 0}
        />
      </div>

      {!summary?.input_minutes_available && (
        <div className="sa-note">
          The station input clock is not reporting yet. Until the StationAgent sends
          idle time, active minutes come only from scans, which the operator controls.
          A blank input column means not measured, never nobody there.
        </div>
      )}

      <div className="sa-floor">
        <h2>On the floor now</h2>
        <div className="sa-floor-grid">
          {floor.map((s) => (
            <div key={s.station_code} className={`sa-station ${s.occupied ? 'occupied' : ''}`}>
              <div className="sa-station-top">
                <span className="sa-station-code">{s.station_code}</span>
                <span className="sa-station-dept">{s.department}</span>
              </div>
              <div className="sa-station-name">{s.station_name}</div>
              <div className="sa-station-who">
                {s.session ? s.session.operator_name : <span className="sa-muted">no one logged in</span>}
              </div>
              {s.session && (
                <div className="sa-station-meta">since {time(s.session.started_at)}</div>
              )}
              {s.pour_session && (
                <div className="sa-station-pour">
                  {s.pour_session.blend_number} &middot; {s.pour_session.status}
                  {Number(s.pour_session.paused_minutes) > 0 &&
                    ` · ${num(s.pour_session.paused_minutes, 0)}m paused`}
                </div>
              )}
              {s.today && (
                <div className="sa-station-today">
                  {int(s.today.units)} units &middot; {num(s.today.active_minutes, 0)} active min
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="sa-tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            className={`sa-tab ${tab === t.key ? 'active' : ''}`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
            {t.key === 'flags' && flags.length > 0 && (
              <span className="sa-badge">{flags.length}</span>
            )}
          </button>
        ))}
      </div>

      {tab === 'stations' && (
        <Table
          empty="No station activity in this range."
          head={['Date', 'Station', 'Dept', 'Operators', 'Carts', 'Units',
                 'Active min', 'Input min', 'Units / active min']}
          rows={stations.map((r) => [
            r.work_date,
            <strong>{r.station_code}</strong>,
            r.department,
            r.operator_names || '--',
            int(r.carts),
            int(r.units),
            num(r.active_minutes, 0),
            r.input_minutes == null ? <span className="sa-muted">not measured</span> : int(r.input_minutes),
            <strong>{num(r.units_per_active_minute, 2)}</strong>
          ])}
        />
      )}

      {tab === 'operators' && (
        <>
          <div className="sa-note">
            <strong>Read the index, not the raw seconds.</strong> Vessels run from 8 to
            53 s/unit, and departments differ too ({norms.map((n) =>
              `${n.department} ${n.median_seconds_per_unit}`).join(', ')} s/unit), so a
            raw rate mostly reports which work someone drew. Mix-adj compares each
            person against the norm <em>for the vessels they actually worked</em>:
            1.00 is typical, below 1.00 is faster. Unmeasurable carts are excluded
            from every rate here, but still counted as work done.
            <br /><br />
            Operators are grouped by department and never ranked across them. A wicker
            compared against carters looks slow for doing a slower job.
          </div>

          {['carting', 'wicking', 'pouring', 'packaging'].map((dept) => {
            const deptRates = rates.filter((r) => r.department === dept);
            if (!deptRates.length) return null;
            const ranked = deptRates
              .filter((r) => r.has_enough_data)
              .sort((a, b) => Number(a.mix_adjusted_index ?? 9) - Number(b.mix_adjusted_index ?? 9));
            const thin = deptRates.filter((r) => !r.has_enough_data);
            return (
              <div key={dept} className="sa-deptblock">
                <h2>{dept}</h2>
                <Table
                  empty={`No measurable ${dept} carts.`}
                  head={['Operator', 'Measurable carts', 'Excluded', 'Units',
                         'Vessels', 'Median s/unit', 'Mix-adj']}
                  rows={ranked.map((r) => [
                    <strong>{r.operator_name}</strong>,
                    int(r.measurable_carts),
                    r.excluded_carts > 0
                      ? <span className="sa-muted">{int(r.excluded_carts)}</span>
                      : '--',
                    int(r.measurable_units),
                    int(r.vessel_types),
                    num(r.median_seconds_per_unit, 1),
                    <strong>{num(r.mix_adjusted_index, 2)}</strong>
                  ])}
                />
                {thin.length > 0 && (
                  <p className="sa-thin">
                    Not enough measurable carts to rate:{' '}
                    {thin.map((r) => `${r.operator_name} (${r.measurable_carts})`).join(', ')}.
                    Shown as unrated rather than given a number built from too little
                    to mean anything.
                  </p>
                )}
              </div>
            );
          })}

          <div className="sa-deptblock">
            <h2>Daily detail</h2>
            <p className="sa-tablenote">
              Active s/unit is how long the work took per unit across the minutes the
              operator was actually active. A claimed s/unit far below it means carts
              were open for only part of the time worked.
            </p>
            <Table
              empty="No operator activity in this range."
              head={['Operator', 'Days', 'Carts', 'Measurable', 'Units', 'Active min',
                     'Active s/unit', 'Utilization', 'Flags']}
              rows={operatorTotals.map((r) => [
                <strong>{r.operator_name}</strong>,
                int(r.days),
                int(r.carts),
                int(r.measurable_carts),
                int(r.units),
                num(r.active_minutes, 0),
                <strong>{num(r.active_seconds_per_unit, 1)}</strong>,
                pct(r.utilization),
                r.instant_closes + r.impossible_rates > 0
                  ? <span className="sa-warn">{r.instant_closes + r.impossible_rates}</span>
                  : '--'
              ])}
            />
          </div>
        </>
      )}

      {tab === 'flags' && (
        <>
          <p className="sa-tablenote">
            A flag means the record cannot be used as a measurement. At scale,
            instant closes are usually a screen workflow habit -- scanning a cart in
            and straight back out -- rather than anyone cheating. Check the pattern
            per department before treating it as a person problem.
          </p>
          <Table
            empty="No flagged carts in this range."
            head={['Date', 'Station', 'Dept', 'Operator', 'Units', 'Elapsed min',
                   's/unit', 'Flag']}
            rows={flags.slice(0, 200).map((r) => [
              r.work_date,
              r.station_code || '--',
              r.department,
              r.operator_name,
              int(r.total_units),
              num(r.elapsed_minutes, 2),
              num(r.seconds_per_unit, 1),
              <span className="sa-warn">
                {[
                  r.instant_close && 'instant close',
                  r.impossible_rate && 'impossible rate',
                  r.stale_open && 'left open',
                  r.carts_closed_together >= 2 && `${r.carts_closed_together} closed together`
                ].filter(Boolean).join(', ')}
              </span>
            ])}
          />
        </>
      )}

      {tab === 'pours' && (
        <>
          {pauses.length > 0 && (
            <div className="sa-pauses">
              <h3>Where pour time goes</h3>
              <div className="sa-pause-list">
                {pauses.map((p) => (
                  <div key={p.reason} className="sa-pause">
                    <span>{p.reason}</span>
                    <strong>{p.pauses}</strong>
                  </div>
                ))}
              </div>
            </div>
          )}
          <Table
            empty="No pour sessions yet. Pours are recorded by scanning the blend sheet at the pouring station."
            head={['Date', 'Blend', 'Station', 'Operator', 'Melter', 'Status',
                   'Elapsed', 'Paused', 'Active', 'Units', 'Active s/unit']}
            rows={pours.map((r) => [
              r.work_date,
              <strong>{r.blend_number || '--'}</strong>,
              r.station_code || '--',
              r.operator_name || '--',
              r.melter_id ?? '--',
              r.status,
              num(r.elapsed_minutes, 0),
              num(r.paused_minutes, 0),
              <strong>{num(r.active_minutes, 0)}</strong>,
              int(r.units_poured),
              num(r.active_seconds_per_unit, 1)
            ])}
          />
        </>
      )}
    </div>
  );
}

function Tile({ label, value, note, warn }) {
  return (
    <div className={`sa-tile ${warn ? 'warn' : ''}`}>
      <div className="sa-tile-label">{label}</div>
      <div className="sa-tile-value">{value}</div>
      {note && <div className="sa-tile-note">{note}</div>}
    </div>
  );
}

function Table({ head, rows, empty }) {
  if (!rows.length) return <div className="sa-empty">{empty}</div>;
  return (
    <div className="sa-tablewrap">
      <table className="sa-table">
        <thead>
          <tr>{head.map((h) => <th key={h}>{h}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((cells, i) => (
            <tr key={i}>{cells.map((c, j) => <td key={j}>{c}</td>)}</tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default StationActivityDashboard;
